import { gql } from '@apollo/client';
import {
    ArticleDocument,
    ArticleFragment,
    ArticleQuery,
    ArticleQueryVariables,
    CardFragment,
    PhotosCarouselGalleryFragment,
} from '../../generated/graphql';
import React, { FC } from 'react';
import styled from 'styled-components';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import Page from '@oberoninternal/travelbase-website/dist/components/Page';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { Box, Flex } from '@rebass/grid';
import ClockIcon from '../icons/ClockIcon';
import HtmlMapper from 'react-html-map';
import tagMap from '../../constants/tagMap';
import RelatedItems from '../RelatedItems';
import Seo from '../Seo';
import { useRouter } from 'next/router';
import PhotosCarousel from '../PhotosCarousel';
import striptags from 'striptags';
import { GetStaticPaths, GetStaticProps } from 'next';
import initApolloClient, { PreviewData } from '../../initApolloClient';
import SmallContentWrapper from '../SmallContentWrapper';
import useAnalyticsContentCategory from '@oberoninternal/travelbase-website/dist/hooks/analytics/useAnalyticsContentCategory';
import { heroTransformToProps } from '../../constants/imageTransforms';
import Hero from '../Hero';
import ContentWrapper from '@oberoninternal/travelbase-website/dist/components/ContentWrapper';
import { Article as ArticleType, WithContext } from 'schema-dts';
import addGlobalQuery from '../../utils/addGlobalQuery';
import { DEFAULT_REVALIDATION_TIME } from '../../constants/revalidationTime';
import { addApolloState } from '@oberoninternal/travelbase-website/dist/createApolloClient';
import getCurrentBaseUrl from '../../utils/getCurrentBaseUrl';
import Head from 'next/head';
import hasRedirect from '../../utils/hasRedirect';

export const fragment = gql`
    query Article($slug: String, $lang: String, $notSlug: String) {
        entry(type: ["article"], slug: [$slug], site: [$lang]) {
            ...Article
        }

        related: entries(slug: [$notSlug], section: ["articles"], limit: 3, orderBy: "postDate DESC", site: [$lang]) {
            ...Card
        }
    }

    query ArticlePaths {
        entries(site: ["*"], section: ["articles"]) {
            __typename
            slug
            language
        }
    }

    fragment Article on article_Entry {
        newsHeroImage {
            ...HeroTransform
        }
        postDate @formatDateTime(format: "j F Y", locale: $lang)
        title
        textBody
        gallery: newsImages {
            ...PhotosCarouselGallery
        }
        settingsCookieConsent
        settingsMetaDescription
        settingsSeoTitle

        id
        parent {
            id
        }
        settingsSearchTags {
            title
        }

        localized {
            url
            language
        }
    }
`;

interface Props {
    data: ArticleQuery;
}

const Article: FC<React.PropsWithChildren<Props>> = ({ data }) => {
    const { related } = data;
    const entry = data.entry as ArticleFragment | null;
    const { asPath, locale } = useRouter();
    useAnalyticsContentCategory('article');

    if (!entry) {
        return null;
    }

    const newsHeroImage = entry.newsHeroImage?.[0];
    const description = entry.textBody && striptags(entry.textBody);
    const schema: WithContext<ArticleType> = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        name: entry.settingsSeoTitle ?? entry.title ?? '',
        description: entry.settingsMetaDescription ?? description ?? '',
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': getCurrentBaseUrl(locale) + (asPath ?? ''),
        },
    };

    return (
        <Page>
            <Head>
                {/* eslint-disable-next-line react/no-danger */}
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
            </Head>
            <Seo
                settingsMetaDescription={entry.settingsMetaDescription ?? description ?? ''}
                settingsSeoTitle={entry.settingsSeoTitle ?? entry.title ?? ''}
                image={newsHeroImage?.ogImageUrl ?? ''}
                alternates={entry.localized}
            />
            {newsHeroImage && <Hero {...heroTransformToProps(newsHeroImage)} />}

            <SmallContentWrapper>
                <Box mt={[6, 8]}>
                    <Date>
                        <Flex alignItems="center" mr={3} as="span">
                            <ClockIcon width={20} height={20} />
                        </Flex>
                        {entry.postDate}
                    </Date>
                </Box>
                <Box mt={[2, 4]} mb={[3, 4]}>
                    <Title variant="large">{entry?.title}</Title>
                </Box>

                {entry?.textBody && <HtmlMapper html={entry.textBody}>{tagMap}</HtmlMapper>}
            </SmallContentWrapper>
            {entry.gallery && entry.gallery.length > 0 && (
                <Box pt={8}>
                    <PhotosCarousel gallery={(entry.gallery as PhotosCarouselGalleryFragment[]) ?? []} />
                </Box>
            )}
            <ContentWrapper>
                {(related?.length ?? 0) > 0 && <RelatedItems relatedItems={related as CardFragment[]} />}
            </ContentWrapper>
        </Page>
    );
};

export const getStaticProps: GetStaticProps = async context => {
    if (context.locale === 'default') return { props: {} };

    const { params = {}, previewData } = context;
    const client = initApolloClient(context.locale as string, { previewData: previewData as PreviewData });
    const lang = context.locale as string;
    const { data } = await client.query<ArticleQuery, ArticleQueryVariables>({
        query: ArticleDocument,
        variables: {
            lang,
            slug: params.slug as string,
            notSlug: `!= ${params.slug}`,
        },
    });

    if (!data.entry) {
        const redirect = await hasRedirect((params?.uri as string[]).join('/'), lang);

        if (redirect) {
            const { redirectDestUrl, redirectHttpCode } = redirect;
            return {
                redirect: {
                    permanent: redirectHttpCode === 301,
                    destination: redirectDestUrl,
                },
                revalidate: DEFAULT_REVALIDATION_TIME,
            };
        }

        return {
            notFound: true,
        };
    }

    return {
        ...addApolloState(
            client,
            (await addGlobalQuery(
                {
                    props: {
                        data,
                    },
                },
                lang
            )) as { props: Record<string, unknown> }
        ),
        revalidate: DEFAULT_REVALIDATION_TIME,
    };
};

export const getStaticPaths: GetStaticPaths = async () => {
    // const client = initApolloClient(defaultLanguage);
    // const { data } = await client.query<ArticlePathsQuery>({
    //     query: ArticlePathsDocument,
    // });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const paths: any[] =
        // data.entries
        //   ?.filter(
        //     entry => entry && popularPages.includes(`/${entry.language ?? defaultLanguage}/news/${entry.slug}/`)
        //   )
        //   .map(entry => {
        //       if (!entry?.slug) {
        //           throw new Error('No article slug');
        //       }
        //       return {
        //           params: {
        //               locale: entry?.language ?? defaultLanguage,
        //               slug: entry?.slug,
        //           },
        //       };
        //   }) ??
        [];

    // eslint-disable-next-line no-console
    // console.log(`News.getStaticPaths: including ${paths.length} out of ${data.entries?.length ?? 0} paths`);

    return {
        paths,
        fallback: 'blocking',
    };
};

const Date = styled(Body)`
    color: ${({ theme }) => theme.colors.neutral[40]};
    display: flex;
`;

export default Article;
